import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FreightListService } from '../cargo-list/cargo-list.service';
import { FilterModel } from 'src/app/core/models/filter';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { PaginationList } from 'src/app/core/models/pagination-list';
import { ListFilterComponent } from 'src/app/shared/list-filter/list-filter.component';
import { DateManager } from 'src/app/core/managers/date.manager';
import { Utils } from 'src/app/core/resources/utils';
import { DateEnum } from 'src/app/core/enums/date.enum';
import { CargoStateEnum } from 'src/app/core/enums/cargoState.enum';
import { NgxSpinnerService } from 'ngx-spinner';
import { ResponseList } from 'src/app/core/interfaces/response-list';

@Component({
  selector: 'app-cargo-prevalidation',
  templateUrl: './cargo-prevalidation.component.html',
  styleUrls: ['./cargo-prevalidation.component.scss']
})
export class CargoPrevalidationComponent implements OnInit {

  @ViewChild(ListFilterComponent) listFilterComponent: ListFilterComponent;
  readonly typeList = 'prevalidation';
  optionsFilter: FilterModel[] = [];
  filters: string = '';
  public paginationList: PaginationList = new PaginationList();
  public listLoading: boolean = true;

  constructor(
    public service: FreightListService,
    public utils: Utils,
    private router: Router,
    private spinner: NgxSpinnerService
  ) { }

  ngOnInit() {
    this.paginationList.setPageSize(25);
    this.optionsFilter = this.service.getOptionsFilter(this.typeList);
  }

  ngOnDestroy(): void {
    this.resetPagging();
  }

  public refreshList($event?: string): void {
    if (this.utils.isDefined($event)) this.filters = $event;
    this.resetPagging();
    this.getListCargo(this.filters, true);
  }

  public onScrollDown(): void {
    if (!this.paginationList.getEnablePagingScroll()) return;
    this.paginationList.setPageKey(this.paginationList.getPageKey() + 1);
    this.getListCargo(this.filters);
  }

  private getListCargo(filters: string, refreshOnLoading: boolean = false): void {
    this.listLoading = this.paginationList.getPageKey() === 1;
    this.spinner.show();
    this.service.getListCargo(
      filters.replace('userClientDocument=', 'numberDocumentCreatorLoad=')
        .replace('prevalidationValid=Pending%20validation', 'prevalidationValid=false')
        .replace('prevalidationValid=Validated', 'prevalidationValid=true'),
      this.paginationList.getPageKey(),
      this.paginationList.getPageSize(),
      this.typeList
    ).subscribe(
      (data: ResponseList<Cargo>) => {
        if (refreshOnLoading) this.resetPagging();
        if (!this.utils.isDefined(data) || !data.results || !data.results.length) {
          this.paginationList.setEnablePagingScroll(false);
          data = { results: [], pagination: { total: 0, pageKey: 1, pageSize: 25 } };
        }
        if (this.paginationList.getList().length) this.paginationList.setList(this.paginationList.getList().concat(data.results));
        else this.paginationList.setList(data.results);
        this.listLoading = false;
      },
      () => {
        this.listLoading = false;
        if (!this.paginationList.getList().length) this.resetPagging();
      },
      () => {
        this.spinner.hide();
      }
    );
  }

  private resetPagging(): void {
    this.paginationList.setPageKey(1);
    this.paginationList.setList([]);
    this.paginationList.setEnablePagingScroll(true);
  }

  public getFinishDate(cargo: Cargo): string {
    return cargo && cargo.finishedByFingerprint && cargo.finishedByFingerprint.date
      ? cargo.finishedByFingerprint.date
      : null;
  }

  public getCreatorDocument(cargo: Cargo): string {
    return cargo && cargo.createdBy && cargo.createdBy.userId
      ? cargo.createdBy.userId
      : cargo.numberDocumentCreatorLoad;
  }

  public getCreatorName(cargo: Cargo): string {
    return cargo && cargo.createdBy && cargo.createdBy.userName
      ? cargo.createdBy.userName
      : null;
  }

  public get someHasCreatorName(): boolean {
    return this.paginationList.getList().some(cargo => !!this.getCreatorName(cargo));
  }
  public get someHasCreatorDocument(): boolean {
    return this.paginationList.getList().some(cargo => !!this.getCreatorDocument(cargo));
  }

  public get isValidatedFilterActive(): boolean {
    return !!(this.filters && this.filters.includes('prevalidationValid=Validated'));
  }

  public getValidationDate(cargo: Cargo): string {
    const fingerprintDate = this.utils.getNestedValue(cargo, 'prevalidation.fingerprint.date');
    if (!fingerprintDate) return null;
    return fingerprintDate;
  }

  public getValidationTooltip(cargo: Cargo): string {
    const userName = this.utils.getNestedValue(cargo, 'prevalidation.fingerprint.userName');
    const userDocument = this.utils.getNestedValue(cargo, 'prevalidation.fingerprint.userId');
    if (!userName || !userDocument) return '';
    return `Validado por: ${userName} (${userDocument})`;
  }

  public isBilled(cargo: Cargo): boolean {
    return cargo && cargo.shippingCost && cargo.shippingCost.cashed;
  }

  public isInRoute(cargo: Cargo): boolean {
    return !!(cargo && cargo.state === CargoStateEnum.START_SERVICE);
  }

  public getDelayTime(cargo: Cargo): { message: string, badge: string } {
    if (!cargo) return { message: null, badge: null };
    if (this.isBilled(cargo)) return { message: "Facturada", badge: "delay-success" };
    const finishDate = this.getFinishDate(cargo);
    if (!finishDate) return { message: "En ruta", badge: "delay-info" };
    const realFinishDate = DateManager.stringToDate(finishDate, DateEnum.YYYY_MM_DD_HH_mm_ZZ);
    const expirationBillDays = cargo.shippingCost && cargo.shippingCost.expirationBillDays ? cargo.shippingCost.expirationBillDays : 1;
    const realFinishDatePlusExpirationBillDays = DateManager.add(realFinishDate, expirationBillDays, "days");
    const diff = DateManager.dateDiff(new Date(), undefined, realFinishDatePlusExpirationBillDays, undefined, 'seconds');
    const remaning = DateManager.durationFormat(diff, "seconds");
    if (!remaning) return { message: "Recien finalizada", badge: "delay-info" };
    let message = "";
    if (remaning.days > 0) message += `${remaning.days}d `;
    if (remaning.hours > 0) message += `${remaning.hours}h `;
    if (remaning.minutes > 0) message += `${remaning.minutes}m`;
    if (message === "") return { message: "Recien finalizada", badge: "delay-info" };
    return { message: message, badge: 'delay-warning' };
  }

  public getDelayDays(cargo: Cargo): number {
    if (!cargo) return null;
    if (this.isBilled(cargo)) return null;
    const finishDate = this.getFinishDate(cargo);
    if (!finishDate) return null;
    const diff = DateManager.dateDiff(new Date(), undefined, finishDate, DateEnum.YYYY_MM_DD_HH_mm_ZZ, 'days');
    if (diff === null) return null;
    return diff > 0 ? diff : 0;
  }

  public getPrevalidationState(cargo: Cargo): 'approved' | 'pending' {
    if (!cargo || !cargo.prevalidation || !this.utils.isDefined(cargo.prevalidation.valid)) return 'pending';
    return cargo.prevalidation.valid ? 'approved' : 'pending';
  }

  public getPrevalidationLabel(cargo: Cargo): string {
    switch (this.getPrevalidationState(cargo)) {
      case 'approved':
        return 'Aprobado';
      default:
        return 'Pendiente';
    }
  }

  public trackByCargo(index: number, cargo: Cargo): string | number {
    return cargo && cargo.id ? cargo.id : cargo && cargo.consecutive ? cargo.consecutive : index;
  }

  public goToDetail(cargo: Cargo): void {
    if (!cargo || !this.utils.isDefined(cargo.consecutive)) return;
    this.spinner.show();
    this.router.navigate([this.utils.getCargoPathByState(cargo).shortPath]).then(() => {
      this.spinner.hide();
    });
  }
}
